/* import __COLOCATED_TEMPLATE__ from './manage-export-schedule-side-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { type Task, type TaskGenerator } from 'ember-concurrency';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import { SchedulePreset } from 'embercom/models/reporting/dataset-export-schedule';
import type DatasetExportSchedule from 'embercom/models/reporting/dataset-export-schedule';

interface Args {
  onClose: () => void;
  isOpen: boolean;
  openEditScheduleSideDrawer: () => void;
  setIsEditing: (row: DatasetExportSchedule) => void;
}

export default class ManageExportScheduleSideDrawer extends Component<Args> {
  @service declare store: Store;
  @service declare intl: IntlService;

  fetchDataTask: Task<any, any> = taskFor(this.fetchData);

  tableData = trackedTask(this, this.fetchDataTask, () => []);

  @dropTask
  *fetchData(): TaskGenerator<any> {
    let schedules = yield this.store.findAll('reporting/dataset-export-schedule');
    return schedules.filter((schedule: DatasetExportSchedule) => !schedule.isNew);
  }

  get data() {
    return (this.tableData?.value as any[])?.rejectBy('isDeleted') || [];
  }

  get columns() {
    return [
      {
        label: this.intl.t('reporting.data-export.schedule-export.schedule-name'),
        valuePath: 'name',
        width: '50%',
      },
      {
        label: this.intl.t('reporting.data-export.schedule-export.frequency'),
        valuePath: 'frequency',
        width: '50%',
      },
    ];
  }

  private get weekDays() {
    return [
      this.intl.t('app.lib.reporting.week-days.sunday'),
      this.intl.t('app.lib.reporting.week-days.monday'),
      this.intl.t('app.lib.reporting.week-days.tuesday'),
      this.intl.t('app.lib.reporting.week-days.wednesday'),
      this.intl.t('app.lib.reporting.week-days.thursday'),
      this.intl.t('app.lib.reporting.week-days.friday'),
      this.intl.t('app.lib.reporting.week-days.saturday'),
    ];
  }

  private frequencyPrefix(schedule: DatasetExportSchedule) {
    if (schedule.exportRule.interval.step === SchedulePreset.DAILY) {
      return this.intl.t(
        'reporting.data-export.schedule-export.new-schedule.frequency-dropdown.daily',
      );
    } else if (schedule.exportRule.interval.step === SchedulePreset.WEEKLY) {
      return this.intl.t(
        'reporting.data-export.schedule-export.new-schedule.frequency-dropdown.weekly',
        {
          dayOfWeek: this.weekDays[schedule.exportRule.daysOfWeek[0]],
        },
      );
    } else {
      return this.intl.t(
        'reporting.data-export.schedule-export.new-schedule.frequency-dropdown.monthly',
        {
          dayOfWeek: this.weekDays[schedule.exportRule.daysOfWeek[0]],
        },
      );
    }
  }

  @action
  buildFrequency(schedule: DatasetExportSchedule) {
    return this.frequencyPrefix(schedule);
  }

  @action
  editSchedule(row: DatasetExportSchedule) {
    this.args.setIsEditing(row);
    this.args.openEditScheduleSideDrawer();
  }

  @action
  deleteSchedule(row: DatasetExportSchedule) {
    row.destroyRecord();
  }

  get hasNoSchedules() {
    return (this.tableData?.value as any[])?.length === 0;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::ManageExportScheduleSideDrawer': typeof ManageExportScheduleSideDrawer;
    'reporting/manage-export-schedule-side-drawer': typeof ManageExportScheduleSideDrawer;
  }
}
